/**
 * Vulnerability Management Service
 *
 * API layer for project/scan management and vulnerability analysis
 * via OWASP Dependency-Track.
 */
import api from '../api/client';

const VulnerabilityManagementService = {
  // ===== Projects =====

  createProject: async (projectName) => {
    return api.post(`/vulnerability-management/projects?projectName=${encodeURIComponent(projectName)}`);
  },

  listProjects: async (pageNumber = 1, pageSize = 10) => {
    return api.get('/vulnerability-management/projects', {
      params: { page_number: pageNumber, page_size: pageSize }
    });
  },

  getProject: async (uuid) => {
    return api.get(`/vulnerability-management/projects/${uuid}`);
  },

  deleteProject: async (uuid) => {
    return api.post(`/vulnerability-management/projects/${uuid}/delete`);
  },

  // ===== Scans =====

  uploadScan: async (projectUuid, componentName, version, sbomFile, isLatest = true) => {
    const formData = new FormData();
    formData.append('sbom', sbomFile);

    const params = new URLSearchParams({
      componentName,
      version,
      isLatest: isLatest.toString()
    });

    return api.post(
      `/vulnerability-management/projects/${projectUuid}/scans?${params.toString()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  deleteScan: async (uuid) => {
    return api.post(`/vulnerability-management/scans/${uuid}/delete`);
  },

  setScanTracked: async (uuid, isLatest) => {
    return api.post(`/vulnerability-management/scans/${uuid}/track?isLatest=${isLatest}`);
  },

  // ===== Overview & Status =====

  getOverview: async () => {
    return api.get('/vulnerability-management/overview');
  },

  checkAnalysisStatus: async (token) => {
    return api.get(`/vulnerability-management/analysis/${token}/status`);
  },

  // ===== Scan Details (Report page) =====

  getScanDetails: async (uuid) => {
    return api.get(`/vulnerability-management/analysis/${uuid}`);
  },

  getAnalysisVulnerabilities: async (uuid, severity = null) => {
    const params = {};
    if (severity) {
      params.severity = severity;
    }
    return api.get(`/vulnerability-management/analysis/${uuid}/vulnerabilities`, { params });
  },

  getAnalysisComponents: async (uuid, pageNumber = 1, pageSize = 100, search = null) => {
    const params = { page_number: pageNumber, page_size: pageSize };
    if (search) {
      params.search = search;
    }
    return api.get(`/vulnerability-management/analysis/${uuid}/components`, { params });
  },

  // ===== Vulnerability Detail =====

  getVulnerabilityDetail: async (source, vulnId) => {
    return api.get(`/vulnerability-management/vulnerability/${source}/${vulnId}`);
  },

  // ===== Notification Preferences =====

  getNotificationPreferences: async () => {
    return api.get('/vulnerability-management/notification-preferences');
  },

  updateNotificationPreferences: async (preferences) => {
    return api.post('/vulnerability-management/notification-preferences', preferences);
  }
};

export default VulnerabilityManagementService;
